import { useEffect, useMemo, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArchiveRestore, Check, Clock, Download, Send, X, Zap } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useArchiveListQuery,
  useArchiveQuery,
  useCreateArchiveMutation,
  useDownloadArchiveMutation,
  useHandoffArchiveMutation,
  type ArchiveBuildStatus,
} from '@/lib/api/archive';
import { formatDateTime } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * T-0115 / E10 — Engagement archive modal (refactor of T-0060).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-round4a.js`
 * → `openArchive(eid)` + `design_handoff_caliber_v2/README.md` §4 modal
 * `data-open-archive` (large side-panel pattern — wide modal, multi-card
 * scrollable body, footer-pinned actions per §8 pattern 1).
 *
 * What this modal does:
 *   - Triggered from the engagement overview (and the legacy archive
 *     tab route) — `data-open-archive` opener pattern.
 *   - Lets the consultant kick a deterministic build of the engagement
 *     archive (E05-F03 / ADR-009 byte-determinism contract).
 *   - Polls the build pipeline (`useArchiveQuery` with `pollMs: 2000`
 *     while the row's `build_status === 'building'`).
 *   - Shows the existing archives list on the left, the active archive's
 *     detail + pipeline-progress + handoff form on the right.
 *
 * Why a modal (not a route): the prototype's archive surface is a
 * side-panel modal (it overlays the engagement context the consultant
 * just was in — closing the modal returns them to the engagement page
 * rather than the dedicated archive route). The legacy `/archive` route
 * is preserved for direct linking; this modal is the prototype-true
 * pattern triggered from the engagement overview.
 *
 * Accessibility: Esc closes, focus-trap via Radix Dialog. Click-outside
 * dismisses (no un-saved local state to guard).
 */

export interface ArchiveModalProps {
  engagementId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ArchiveModal({ engagementId, open, onOpenChange }: ArchiveModalProps) {
  const list = useArchiveListQuery(open ? engagementId : undefined);
  const create = useCreateArchiveMutation(engagementId);
  const [activeArchiveId, setActiveArchiveId] = useState<string | null>(null);
  // Poll while the archive is building. The query auto-stops once the
  // row reports a terminal state (we recompute the interval each render).
  const detail = useArchiveQuery(open ? engagementId : undefined, activeArchiveId, {
    pollMs: activeArchiveId ? 2000 : undefined,
  });
  const handoff = useHandoffArchiveMutation(engagementId, activeArchiveId);

  // Auto-select the first archive on open if one exists and nothing is
  // already selected — so the right-hand detail pane isn't blank.
  useEffect(() => {
    if (!open) return;
    if (activeArchiveId) return;
    const first = list.data?.items?.[0];
    if (first) setActiveArchiveId(first.archive_id);
  }, [open, list.data, activeArchiveId]);

  // Reset state when the modal closes so the next open is fresh.
  useEffect(() => {
    if (!open) {
      setActiveArchiveId(null);
    }
  }, [open]);

  const archive = detail.data;
  const isBuilding = archive?.build_status === 'building';
  const isSucceeded = archive?.build_status === 'succeeded';

  async function startBuild() {
    const r = await create.mutateAsync();
    setActiveArchiveId(r.archive_id);
  }

  async function submitHandoff(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const recipient = String(form.get('recipient') ?? '').trim();
    if (!recipient) return;
    await handoff.mutateAsync({ recipient, delivery_method: 'signed_link' });
  }

  const titleId = `archive-modal-title-${engagementId}`;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1100px,95vw)] max-h-[92vh] min-h-[60vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
            <div className="min-w-0">
              <div className="text-meta uppercase tracking-wide text-ink-500">
                Engagement archive
              </div>
              <Dialog.Title id={titleId} className="mt-1 text-h2 font-semibold text-ink-900">
                Build engagement archive
              </Dialog.Title>
              <div className="mt-1 text-meta text-ink-500">
                Same contents every build · SHA-256 manifest · delivered by signed link
              </div>
            </div>
            <button
              type="button"
              onClick={startBuild}
              disabled={create.isPending}
              className={cn(
                'inline-flex h-9 items-center gap-1.5 rounded-card bg-ink-900 px-3 text-body-sm font-medium text-paper-0',
                'hover:bg-ink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {create.isPending ? <Spinner /> : <Zap className="h-4 w-4" aria-hidden="true" />}
              <span>Build new archive</span>
            </button>
          </div>

          <div className="grid gap-0 overflow-hidden md:grid-cols-[280px_1fr]">
            <ArchiveList
              isLoading={list.isLoading}
              error={list.error}
              items={list.data?.items}
              activeArchiveId={activeArchiveId}
              onPick={setActiveArchiveId}
            />

            <div className="overflow-y-auto px-6 py-5">
              {create.error ? <ErrorMessage error={create.error} /> : null}

              {!activeArchiveId ? (
                <EmptyState
                  title="No archive selected"
                  description={
                    list.data?.items?.length
                      ? 'Pick an archive on the left to view its build state.'
                      : 'Kick a build to get started — the archive is engagement-deterministic.'
                  }
                />
              ) : detail.isLoading && !detail.data ? (
                <div className="flex items-center gap-2 text-body-sm text-ink-500">
                  <Spinner /> Loading…
                </div>
              ) : detail.error ? (
                <ErrorMessage error={detail.error} />
              ) : archive ? (
                <div className="space-y-4">
                  <ArchivePipelineCard
                    status={archive.build_status}
                    builtAt={archive.built_at}
                    buildError={archive.build_error}
                    isBuilding={isBuilding}
                  />

                  <ArchiveInventoryCard
                    archiveId={archive.archive_id}
                    folderSchemaVersion={archive.folder_schema_version}
                    contentHash={archive.archive_content_hash}
                    builtBy={archive.built_by}
                    createdAt={archive.created_at}
                  />

                  {archive.manifest ? <ManifestPreview manifest={archive.manifest} /> : null}

                  {isSucceeded ? (
                    <HandoffCard
                      archiveId={archive.archive_id}
                      downloadUrl={archive.download_url}
                      handedOffAt={archive.handed_off_at}
                      onSubmit={submitHandoff}
                      pending={handoff.isPending}
                      error={handoff.error}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
            <div className="text-meta text-ink-500">
              Re-running this build with the same inputs produces a byte-identical archive.
            </div>
            <Dialog.Close
              className={cn(
                'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
              )}
            >
              Close
            </Dialog.Close>
          </div>

          <Dialog.Close
            aria-label="Close archive modal"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ArchiveList({
  isLoading,
  error,
  items,
  activeArchiveId,
  onPick,
}: {
  isLoading: boolean;
  error: unknown;
  items: { archive_id: string; build_status: ArchiveBuildStatus; created_at: string }[] | undefined;
  activeArchiveId: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <aside className="overflow-y-auto border-r border-ink-100 bg-paper-50 px-4 py-5">
      <div className="text-meta uppercase tracking-wide text-ink-500">Archives</div>
      <div className="mt-2">
        {isLoading ? (
          <div className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading…
          </div>
        ) : error ? (
          <ErrorMessage error={error} />
        ) : !items || items.length === 0 ? (
          <p className="mt-2 text-body-sm text-ink-500">No archives yet.</p>
        ) : (
          <ul className="space-y-1">
            {items.map((row) => (
              <li key={row.archive_id}>
                <button
                  type="button"
                  onClick={() => onPick(row.archive_id)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-card border px-3 py-2 text-left text-body-sm transition-colors',
                    activeArchiveId === row.archive_id
                      ? 'border-ink-200 bg-paper-0 text-ink-900'
                      : 'border-transparent text-ink-700 hover:border-ink-100 hover:bg-paper-0',
                  )}
                >
                  <span className="font-mono text-meta text-ink-700">
                    {row.archive_id.slice(0, 8)}…
                  </span>
                  <StatusPill status={row.build_status} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function StatusPill({ status }: { status: ArchiveBuildStatus }) {
  const tone =
    status === 'succeeded'
      ? 'bg-posture-healthy-tint text-posture-healthy'
      : status === 'failed'
        ? 'bg-posture-critical-tint text-posture-critical'
        : 'bg-ink-100 text-ink-700';
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center rounded-full px-2.5 text-meta font-medium',
        tone,
      )}
    >
      {status === 'building' ? 'Building…' : status === 'succeeded' ? 'Sealed' : 'Failed'}
    </span>
  );
}

function ArchivePipelineCard({
  status,
  builtAt,
  buildError,
  isBuilding,
}: {
  status: ArchiveBuildStatus;
  builtAt: string | null;
  buildError: string | null;
  isBuilding: boolean;
}) {
  const steps = [
    { n: 1, lbl: 'Reconcile registers', sub: 'Per-register CSV + JSON' },
    { n: 2, lbl: 'Bundle parsed trees', sub: 'Markdown per source doc' },
    { n: 3, lbl: 'Render snapshot PDFs', sub: 'PDF + structured per snapshot' },
    { n: 4, lbl: 'Hash + manifest', sub: 'SHA-256 per file' },
    { n: 5, lbl: 'Sign & seal archive', sub: 'Signed-link delivery' },
  ];
  // We don't get a real progress signal from the backend in v1, so phase
  // mapping is coarse: building shows step 3 in-flight; terminal states
  // render the whole pipeline as complete or failed.
  const activePhase = status === 'building' ? 3 : status === 'succeeded' ? 6 : 0;

  return (
    <section
      className={cn(
        'rounded-card border border-ink-100 bg-paper-0 p-4',
        status === 'failed' && 'border-l-4 border-l-posture-critical',
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-h3 font-semibold text-ink-900">Build pipeline</div>
          <div className="mt-0.5 text-meta text-ink-500">
            {status === 'building'
              ? 'Building…'
              : status === 'succeeded'
                ? `Sealed${builtAt ? ` · ${formatDateTime(builtAt)}` : ''}`
                : `Failed${buildError ? ` · ${buildError}` : ''}`}
          </div>
        </div>
        {isBuilding ? (
          <span className="inline-flex items-center gap-1.5 text-meta text-ink-500">
            <Spinner /> Polling…
          </span>
        ) : null}
      </div>
      <ol className="mt-4 grid gap-2 sm:grid-cols-5">
        {steps.map((s) => {
          const done = activePhase > s.n;
          const running = activePhase === s.n;
          return (
            <li
              key={s.n}
              className={cn(
                'rounded-card border px-3 py-2 text-meta',
                done
                  ? 'border-posture-healthy/40 bg-posture-healthy-tint text-posture-healthy'
                  : running
                    ? 'border-accent-amber-600 bg-accent-amber-50 text-accent-amber-700'
                    : 'border-ink-100 bg-paper-50 text-ink-500',
              )}
            >
              <div className="font-mono text-[10px]">{s.n.toString().padStart(2, '0')}</div>
              <div className="mt-0.5 font-medium text-ink-900">{s.lbl}</div>
              <div className="text-ink-500">{s.sub}</div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function ArchiveInventoryCard({
  archiveId,
  folderSchemaVersion,
  contentHash,
  builtBy,
  createdAt,
}: {
  archiveId: string;
  folderSchemaVersion: string;
  contentHash: string | null;
  builtBy: string;
  createdAt: string;
}) {
  return (
    <section className="rounded-card border border-ink-100 bg-paper-0 p-4">
      <div className="text-h3 font-semibold text-ink-900">Build metadata</div>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-body-sm sm:grid-cols-2">
        <div>
          <dt className="text-meta uppercase tracking-wide text-ink-500">archive_id</dt>
          <dd className="font-mono text-ink-900">{archiveId}</dd>
        </div>
        <div>
          <dt className="text-meta uppercase tracking-wide text-ink-500">folder_schema</dt>
          <dd className="font-mono text-ink-700">{folderSchemaVersion}</dd>
        </div>
        <div>
          <dt className="text-meta uppercase tracking-wide text-ink-500">archive_content_hash</dt>
          <dd className="break-all font-mono text-ink-700">{contentHash ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-meta uppercase tracking-wide text-ink-500">built_by</dt>
          <dd className="font-mono text-ink-700">{builtBy.slice(0, 8)}…</dd>
        </div>
        <div>
          <dt className="text-meta uppercase tracking-wide text-ink-500">created_at</dt>
          <dd className="text-ink-700">{formatDateTime(createdAt)}</dd>
        </div>
      </dl>
    </section>
  );
}

function ManifestPreview({ manifest }: { manifest: unknown }) {
  // Render the manifest as pretty-printed JSON. Defensive — manifest is
  // jsonb; the modal renders whatever shape the backend returns.
  const preview = useMemo(() => {
    try {
      return JSON.stringify(manifest, null, 2);
    } catch {
      return '(unable to render manifest)';
    }
  }, [manifest]);
  return (
    <section className="rounded-card border border-ink-100 bg-paper-0 p-4">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-h3 font-semibold text-ink-900">manifest.json</div>
          <div className="mt-0.5 text-meta text-ink-500">
            Deterministic byte order · re-running this build produces a byte-identical archive
          </div>
        </div>
      </div>
      <pre className="mt-3 max-h-72 overflow-auto rounded-card border border-ink-100 bg-paper-50 p-3 font-mono text-[11px] leading-5 text-ink-900">
        {preview}
      </pre>
    </section>
  );
}

function HandoffCard({
  archiveId,
  downloadUrl,
  handedOffAt,
  onSubmit,
  pending,
  error,
}: {
  archiveId: string;
  downloadUrl: string | null;
  handedOffAt: string | null;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  pending: boolean;
  error: unknown;
}) {
  const sent = handedOffAt !== null;
  const download = useDownloadArchiveMutation();
  return (
    <section className="rounded-card border border-ink-100 bg-paper-0 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-h3 font-semibold text-ink-900">Hand-off delivery</div>
          <div className="mt-0.5 text-meta text-ink-500">
            {sent
              ? `Handed off ${formatDateTime(handedOffAt)} · audited`
              : 'A signed-link with engagement-scoped TTL gets sent to the recipient.'}
          </div>
        </div>
        {downloadUrl ? (
          <button
            type="button"
            onClick={() => download.mutate({ archive_id: archiveId, download_url: downloadUrl })}
            disabled={download.isPending}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-meta font-medium text-ink-900',
              'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {download.isPending ? (
              <Spinner />
            ) : (
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            <span>Download archive</span>
          </button>
        ) : null}
      </div>
      {download.error ? (
        <div className="mt-3">
          <ErrorMessage error={download.error} />
        </div>
      ) : null}
      {sent ? (
        <p className="mt-3 inline-flex items-center gap-1.5 text-body-sm text-posture-healthy">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          Hand-off recorded.
        </p>
      ) : (
        <form className="mt-3 space-y-2" onSubmit={onSubmit}>
          <Label htmlFor="archive-recipient">Recipient</Label>
          <Input
            id="archive-recipient"
            name="recipient"
            type="email"
            placeholder="recipient@example.com"
            required
          />
          {error ? <ErrorMessage error={error} /> : null}
          <button
            type="submit"
            disabled={pending}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-card bg-ink-900 px-3 text-body-sm font-medium text-paper-0',
              'hover:bg-ink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {pending ? <Spinner /> : <Send className="h-3.5 w-3.5" aria-hidden="true" />}
            <span>Record handoff</span>
          </button>
        </form>
      )}
    </section>
  );
}

/**
 * Unused export — kept for icon-tree-shake stability (lucide-react
 * tree-shakes by name, but importing icons we don't render keeps the
 * primitive ready for the prototype's pipeline header iconography in
 * a follow-up). The Clock + ArchiveRestore icons are part of the
 * prototype but not yet wired into our v1 surface.
 */
export const __ARCHIVE_MODAL_ICONS = { Clock, ArchiveRestore };
